import {
  NUTRIENT_COLORS,
  NUTRIENT_DEEP_COLORS,
  NUTRIENT_LABELS,
  NUTRIENT_UNITS,
  TARGETED,
  formatNutrient,
  formatShare,
  macroLine,
  macroShare,
  pastTargetLap,
  type GoalOnDay,
  type Nutrients,
  type Targeted,
} from '~/data/nutrition'

/** How big a dial stands, and how thick its ring is drawn — the phone's, at one to one. */
const DIAL = 68
const STROKE = 7

/**
 * What this much of a food comes to, each nutrient on a dial of its own — the nutrition spec,
 * Logging an entry, and the same dials wherever a food, an entry or a meal is read.
 *
 * The circle is the day's target for that nutrient. What the day already holds fills it from
 * the top in a faded band; what this amount would add carries straight on from there in the
 * full colour, so the slice is read as the thing being decided — how far this one press moves
 * the day. Past the target it comes round again in the nutrient's deeper cut. Over each dial
 * stands what the day would have left once this much is logged, going negative past the
 * target; under it, the nutrient's name, and for a macro what share of the calories it is.
 *
 * A nutrient with no target has no circle to be a share of, so the ring is drawn as what this
 * amount is made of instead: with no goal there is nothing to be a share of but itself.
 */
export function FoodDials({
  comesTo: comesToSoFar,
  dayTotalWithout = null,
  goal = null,
  label = 'This comes to',
}: {
  /**
   * What the amount comes to, or null while there is no amount to speak of. A quick entry
   * states only what was typed, and the rest stands as unknown.
   */
  comesTo: (Partial<Nutrients> & { calories: number }) | null
  /** What the day holds apart from this, where the dials are read against a day. */
  dayTotalWithout?: Nutrients | null
  goal?: GoalOnDay | null
  /** What the row is, for ears. */
  label?: string
}) {
  const comesTo: Nutrients | null =
    comesToSoFar === null
      ? null
      : {
          protein: null,
          carbs: null,
          fat: null,
          fibre: null,
          sugar: null,
          saturatedFat: null,
          sodium: null,
          ...comesToSoFar,
        }
  return (
    <div role="group" aria-label={label} className="flex items-start justify-between gap-2">
      {/* The same four figures in one line, for a reader that takes a sentence over four dials. */}
      <span className="sr-only">{comesTo === null ? 'nothing' : macroLine(comesTo)}</span>
      {TARGETED.map((name) => (
        <Dial
          key={name}
          name={name}
          adding={comesTo?.[name] ?? null}
          logged={dayTotalWithout?.[name] ?? 0}
          target={goal === null || goal[name] <= 0 ? null : goal[name]}
          share={comesTo === null || name === 'calories' ? null : macroShare(comesTo, name)}
        />
      ))}
    </div>
  )
}

function Dial({
  name,
  adding,
  logged,
  target,
  share,
}: {
  name: Targeted
  adding: number | null
  logged: number
  target: number | null
  /** What share of the calories this macro is, for the line under the name and the no-goal ring. */
  share: number | null
}) {
  const colour = NUTRIENT_COLORS[name]
  const left = target !== null && adding !== null ? target - logged - adding : null
  const ofTarget = target !== null && adding !== null ? adding / target : null
  const radius = DIAL / 2 - STROKE / 2
  const around = 2 * Math.PI * radius
  const held = target === null ? 0 : Math.min(1, Math.max(0, logged / target))
  const slice =
    target === null
      ? name === 'calories'
        ? adding === null
          ? 0
          : 1
        : (share ?? 0)
      : Math.max(0, Math.min(1, (logged + (adding ?? 0)) / target) - held)
  const past = target === null ? 0 : pastTargetLap(logged + (adding ?? 0), target)
  const figure =
    adding === null
      ? '—'
      : name === 'calories'
        ? Math.round(adding).toLocaleString()
        : formatNutrient(name, adding)
  const said = [
    `${NUTRIENT_LABELS[name]} ${adding === null ? 'unknown' : formatNutrient(name, adding)}`,
    ...(ofTarget === null ? [] : [`${formatShare(ofTarget)} of the target`]),
    ...(left === null ? [] : [`${formatNutrient(name, left)} left`]),
  ].join(', ')
  const arc = (from: number, sweep: number) => ({
    strokeDasharray: `${String(Math.max(0, sweep) * around)} ${String(around)}`,
    strokeDashoffset: String(-from * around),
  })

  return (
    <div aria-label={said} className="flex min-w-0 flex-1 flex-col items-center gap-1">
      {/* What the day would have left: empty where there is no target, drawn all the same so
          the four stand level. */}
      <span
        className="h-4 text-[11px] tracking-wider tabular-nums"
        style={{ color: colour, opacity: 0.7 }}
      >
        {left === null ? '' : formatNutrient(name, left)}
      </span>
      <span className="relative grid place-items-center" style={{ width: DIAL, height: DIAL }}>
        <svg
          aria-hidden
          viewBox={`0 0 ${String(DIAL)} ${String(DIAL)}`}
          className="absolute inset-0 -rotate-90"
          style={{ width: DIAL, height: DIAL }}
        >
          <circle
            cx={DIAL / 2}
            cy={DIAL / 2}
            r={radius}
            fill="none"
            strokeWidth={STROKE}
            className="stroke-white/15"
          />
          {held > 0 && (
            <circle
              data-held
              cx={DIAL / 2}
              cy={DIAL / 2}
              r={radius}
              fill="none"
              strokeWidth={STROKE}
              stroke={colour}
              strokeOpacity={0.3}
              className="transition-[stroke-dasharray] duration-500"
              {...arc(0, held)}
            />
          )}
          {slice > 0 && (
            <circle
              data-slice
              cx={DIAL / 2}
              cy={DIAL / 2}
              r={radius}
              fill="none"
              strokeWidth={STROKE}
              stroke={colour}
              className="transition-[stroke-dasharray,stroke-dashoffset] duration-500"
              {...arc(held, slice)}
            />
          )}
          {past > 0 && (
            <circle
              data-lap
              cx={DIAL / 2}
              cy={DIAL / 2}
              r={radius}
              fill="none"
              strokeWidth={STROKE}
              stroke={NUTRIENT_DEEP_COLORS[name]}
              className="transition-[stroke-dasharray] duration-500"
              {...arc(0, past)}
            />
          )}
        </svg>
        <span className="relative max-w-[calc(100%-1rem)] truncate text-center text-sm font-semibold tabular-nums">
          {figure}
        </span>
      </span>
      <span className="text-[11px] font-medium" style={{ color: colour }}>
        {NUTRIENT_LABELS[name]}
      </span>
      {/* The calories are no share of themselves: their unit stands here instead. */}
      <span className="h-4 text-[11px] text-ink-muted">
        {name === 'calories'
          ? NUTRIENT_UNITS.calories
          : share === null
            ? NUTRIENT_UNITS[name]
            : formatShare(share)}
      </span>
    </div>
  )
}
